import { useState } from "react";
import { TypedFilter } from "odata-query";
import { subscriptionService } from "@miplatform/api";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, IRoleListItemResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getRolesUUID = createUUID();
const getRecentRolesUUID = createUUID();

export const useRoles = ({
  debouncedSearch = "",
  modulePermission,
}: IUseRolesProps = {}) => {
  const stateManager = useComponentState();

  const [roles, setRoles] = useState<IRoleListItemResponse[]>([]);
  const [recentRoles, setRecentRoles] = useState<IRoleListItemResponse[]>([]);
  const [filter, setFilter] = useState<TypedFilter<IRoleListItemResponse>>({});

  const getRoles = ({
    top,
    skip,
    sort,
    filters = {},
    params,
    signal,
  }: IApiRequest = {}) => {
    stateManager.abort(getRolesUUID);

    const combinedFilters = {
      ...filter,
      ...filters,
      name: { contains: debouncedSearch },
    };

    if (modulePermission) {
      return stateManager.run(async () => {
        return subscriptionService.roles.listByPermission({
          ...params,
          permission: modulePermission,
          sort: sort ?? "name",
          top,
          skip,
          filters: combinedFilters,
          signal,
        });
      }, getRolesUUID);
    }

    return stateManager.run(async () => {
      return subscriptionService.roles.getAll({
        sort: sort ?? "name",
        top,
        skip,
        filters: combinedFilters,
        ...params,
        signal,
      });
    }, getRolesUUID);
  };

  const getRecentRoles = ({ sort, filters, top, skip }: IApiRequest = {}) => {
    stateManager.abort(getRecentRolesUUID);

    const combinedFilters = {
      ...filter,
      ...filters,
      name: { contains: debouncedSearch },
    };

    if (modulePermission) {
      return stateManager.run(async () => {
        return subscriptionService.roles.listByPermission({
          permission: modulePermission,
          sort: sort ?? "name",
          top,
          skip,
          filters: combinedFilters,
        });
      }, getRecentRolesUUID);
    }

    return stateManager.run(async () => {
      return subscriptionService.roles.getAll({
        sort,
        filters: combinedFilters,
        top,
      });
    }, getRecentRolesUUID);
  };

  return {
    roles,
    getRoles,
    setRoles,
    recentRoles,
    getRecentRoles,
    setRecentRoles,
    filter,
    setFilter,
    loading: stateManager.isBusy(),
  };
};

interface IUseRolesProps {
  debouncedSearch?: string;
  modulePermission?: string;
}
